import { computed, reactive, Ref, shallowRef, ShallowRef, toRef } from 'vue';
import {
  WidgetContainerType,
  WidgetSize,
  WidgetStatus,
  WidgetStatusSetting,
  WidgetType,
} from '../interface/WidgetType';
import { definedWidgetMeta, getWidgetInfo, WidgetMeta } from './widgetManager';

interface WidgetContainerSetting<E, T extends WidgetType> {
  // 表单当前状态
  status: WidgetStatus;
  // 表单字段配置
  fields: {
    [id: string]: Field<E, T>;
  };
  // 是否能切换状态
  canToggle: boolean;
}

interface Field<E, T extends WidgetType> {
  groupName: string;
  title: string;
  desc: string;
  propName: string;
  // todo： 字段状态根据一个队列决定，有不同的优先级，如原始状态，场景状态，变更状态
  statusSettings: Record<WidgetStatus, WidgetStatusSetting>;
  order: number;
  widget: T;
  extra: E;
}

export interface WidgetInfo {
  id: string;
  title: string;
  desc: string;
  groupName: string;
  status: WidgetStatus;
  size: WidgetSize;
  order: number;
}

export interface WidgetItem<T extends WidgetType> extends WidgetInfo {
  component: ShallowRef;
  props: {
    propName: string;
    info: WidgetInfo;
    widget: T;
  };
}

export interface WidgetItemGroup<T extends WidgetType> {
  title: string;
  items: WidgetItem<T>[];
}

export type AddFieldParam = Partial<Pick<Field<unknown, WidgetType>, 'groupName' | 'desc' | 'propName' | 'order'>> & {
  statusSettings?: Partial<Record<WidgetStatus, WidgetStatusSetting>>
}

export class WidgetContainer<E, T extends WidgetType> {
  protected readonly setting: WidgetContainerSetting<E, T>;
  public readonly canToggle: Ref<boolean>;
  public readonly status: Ref<WidgetStatus>;
  public readonly fields: Ref<Record<string, Field<E, T>>>;
  private fieldOrder = 0;

  constructor(status: WidgetStatus, private type: WidgetContainerType) {
    this.setting = reactive({
      status,
      canToggle: true,
      fields: {},
      widgetStatusSetting: {
        show: {},
        edit: {},
      },
    });
    this.canToggle = toRef(this.setting, 'canToggle');
    this.status = toRef(this.setting, 'status');
    this.fields = toRef(this.setting, 'fields');
  }

  public readonly widgetItemMap = computed(() => {
    const map: Record<string, WidgetItem<T>> = {};
    Object.entries(this.setting.fields)
      .forEach(([id, field]) => {
        const widgetStatus = field.statusSettings[this.setting.status];
        if (widgetStatus === 'none') {
          return;
        }
        let setting: {
          meta: WidgetMeta
          component: any;
        } | undefined;
        if (field.widget.name === 'custom') {
          setting = {
            meta: definedWidgetMeta(field.widget.meta),
            component: field.widget.component,
          };
        } else {
          setting = getWidgetInfo(this.type, field.widget.name);
        }
        if (!setting) {
          console.error('组件不存在', this.type, field.widget.name);
          return;
        }
        const info: WidgetInfo = {
          id,
          order: field.order,
          title: field.title,
          desc: field.desc,
          status: widgetStatus,
          groupName: field.groupName,
          size: setting.meta.size[widgetStatus],
        };
        map[id] = {
          ...info,
          component: shallowRef(setting.component),
          props: {
            info,
            widget: field.widget,
            propName: field.propName,
          },
        };
      });
    return map;
  });
  public readonly groupMap = computed(() => {
    const groupsMap: Record<string, WidgetItemGroup<T>> = {};
    Object.values(this.widgetItemMap.value)
      .map(item => {
        if (!groupsMap[item.groupName]) {
          groupsMap[item.groupName] = {
            title: item.groupName,
            items: [],
          };
        }
        groupsMap[item.groupName].items.push(item);
      });
    return groupsMap;
  });

  toggle(status?: WidgetStatus) {
    if (this.setting.canToggle) {
      if (status) {
        this.setting.status = status;
      } else if (this.setting.status === 'edit') {
        this.setting.status = 'show';
      } else {
        this.setting.status = 'edit';
      }
    }
  }

  protected doAddField(id: string, title: string, widget: T, extra: E, param?: AddFieldParam) {
    const hasWidget = getWidgetInfo(this.type, widget.name) !== undefined || widget.name === 'custom';
    this.setting.fields[id] = {
      title, widget, extra,
      propName: param?.propName || id,
      groupName: param?.groupName || '',
      desc: param?.desc || '',
      order: param?.order || this.fieldOrder++,
      statusSettings: {
        edit: !hasWidget ? 'none' : param?.statusSettings?.edit || 'edit',
        show: !hasWidget ? 'none' : param?.statusSettings?.show || 'show',
      },
    };
  }

  protected getExtra(id: string) {
    return this.setting.fields[id].extra;
  }

  getField<GT extends T>(id: string): Field<E, GT> {
    if (!this.setting.fields[id]) {
      throw new Error(id + '不存在');
    }
    return this.setting.fields[id] as Field<E, GT>;
  }

  /**
   * 删除字段
   * @param ids
   */
  delField(...ids: string[]) {
    ids.map(id => delete this.setting.fields[id]);
  }

  hiddenFields(...ids: string[]) {
    ids.map(id => {
      if (this.setting.fields[id]) {
        this.setting.fields[id].statusSettings = {
          show: 'none',
          edit: 'none',
        };
      }
    });
  }

  showFields(...ids: string[]) {
    ids.map(id => {
      if (this.setting.fields[id]) {
        this.setting.fields[id].statusSettings = {
          show: 'show',
          edit: 'edit',
        };
      }
    });
  }

  hiddenFieldsExclude(...ids: string[]) {
    Object.keys(this.setting.fields).map(id => {
      if (!ids.includes(id)) {
        this.setting.fields[id].statusSettings = {
          show: 'none',
          edit: 'none',
        };
      }
    });
  }

  /**
   * 更新字段
   * @param prop
   * @param value
   * @param id
   * @param exclude
   */
  updateField<Prop extends keyof Field<E, T>>(prop: Prop, value: Field<E, T>[Prop], id: string[] | string, exclude = false) {
    const ids = typeof id === 'string' ? [id] : id;
    if (exclude) {
      Object.keys(this.setting.fields).map(id => {
        if (!ids.includes(id)) {
          this.setting.fields[id][prop] = value;
        }
      });
    } else {
      ids.map(id => {
        this.setting.fields[id][prop] = value;
      });
    }
  }
}




